/**
 * India Standard Time (IST) Utility
 * GMT+5:30 timezone support for ANANT WEB PORTAL
 */

// IST offset in milliseconds (5 hours 30 minutes)
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

/**
 * Get current date/time in IST
 */
export function nowIST() {
  const now = new Date();
  return new Date(now.getTime() + IST_OFFSET_MS + now.getTimezoneOffset() * 60 * 1000);
}

/**
 * Convert any date to IST
 */
export function toIST(date) {
  if (!date) return null;
  const d = new Date(date);
  return new Date(d.getTime() + IST_OFFSET_MS + d.getTimezoneOffset() * 60 * 1000);
}

/**
 * Convert IST date to UTC
 */
export function istToUTC(istDate) {
  if (!istDate) return null;
  const d = new Date(istDate);
  return new Date(d.getTime() - IST_OFFSET_MS - d.getTimezoneOffset() * 60 * 1000);
}

/**
 * Format date in IST for display
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 */
export function formatIST(date, options = {}) {
  if (!date) return '';
  
  const defaultOptions = {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };
  
  return new Intl.DateTimeFormat('en-IN', defaultOptions).format(new Date(date));
}

/**
 * Format date only in IST
 */
export function formatDateIST(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(new Date(date));
}

/**
 * Format time only in IST
 */
export function formatTimeIST(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(new Date(date));
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeIST(date) {
  if (!date) return '';
  
  const now = new Date();
  const target = new Date(date);
  const diffMs = target - now;
  const diffSec = Math.abs(diffMs / 1000);
  const diffMin = diffSec / 60;
  const diffHour = diffMin / 60;
  const diffDay = diffHour / 24;
  
  const isPast = diffMs < 0;
  const prefix = isPast ? '' : 'in ';
  const suffix = isPast ? ' ago' : '';
  
  if (diffSec < 60) {
    return isPast ? 'Just now' : 'Starting soon';
  } else if (diffMin < 60) {
    const mins = Math.floor(diffMin);
    return `${prefix}${mins} min${mins > 1 ? 's' : ''}${suffix}`;
  } else if (diffHour < 24) {
    const hours = Math.floor(diffHour);
    return `${prefix}${hours} hour${hours > 1 ? 's' : ''}${suffix}`;
  } else if (diffDay < 7) {
    const days = Math.floor(diffDay);
    return `${prefix}${days} day${days > 1 ? 's' : ''}${suffix}`;
  } else {
    return formatDateIST(date);
  }
}

/**
 * Get IST date string for input fields (YYYY-MM-DD)
 */
export function getISTDateString(date) {
  const d = date ? toIST(new Date(date)) : nowIST();
  return d.toISOString().split('T')[0];
}

/**
 * Get IST time string for input fields (HH:MM)
 */
export function getISTTimeString(date) {
  const d = date ? toIST(new Date(date)) : nowIST();
  return d.toTimeString().slice(0, 5);
}

/**
 * Parse IST date and time strings to UTC Date
 * @param {string} dateStr - YYYY-MM-DD format
 * @param {string} timeStr - HH:MM format
 */
export function parseISTDateTime(dateStr, timeStr = '00:00') {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  
  // Create date in IST
  const istDate = new Date(year, month - 1, day, hour, minute);
  
  // Convert to UTC by subtracting IST offset
  return new Date(istDate.getTime() - IST_OFFSET_MS);
}

/**
 * Format countdown timer
 */
export function formatCountdown(endDate) {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end - now;
  
  if (diff <= 0) return 'Ended';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((diff % (1000 * 60)) / 1000);
  
  if (days > 0) {
    return `${days}d ${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h ${mins}m ${secs}s`;
  } else if (mins > 0) {
    return `${mins}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
}

export default {
  nowIST,
  toIST,
  istToUTC,
  formatIST,
  formatDateIST,
  formatTimeIST,
  formatRelativeIST,
  getISTDateString,
  getISTTimeString,
  parseISTDateTime,
  formatCountdown
};
